import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { animate, useMotionValue, useReducedMotion } from 'framer-motion'
import type { PanInfo } from 'framer-motion'

const ease = [0.23, 1, 0.32, 1] as const
const AUTOPLAY_MS = 6000
const DESKTOP_MIN_WIDTH = 720

export function useVolumeCarousel(count: number, autoplay: boolean) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const prevWidthRef = useRef(0)
  const draggedRef = useRef(false)
  const [width, setWidth] = useState(0)
  const [index, setIndex] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [touched, setTouched] = useState(false)
  const [focusWithin, setFocusWithin] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const reduceMotion = useReducedMotion() ?? false
  const x = useMotionValue(0)

  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const update = () => setWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const isDesktop = width >= DESKTOP_MIN_WIDTH
  const slideWidth = isDesktop ? width / 3 : width * 0.8

  const targetFor = useCallback(
    (i: number) => -(i * slideWidth) + (width - slideWidth) / 2,
    [slideWidth, width],
  )

  const snap = useCallback(
    (i: number) => {
      const target = targetFor(i)
      // Jump instantly on first measure, on resize, or when motion is reduced.
      if (reduceMotion || prevWidthRef.current !== width) {
        prevWidthRef.current = width
        x.set(target)
        return undefined
      }
      return animate(x, target, { duration: 0.3, ease })
    },
    [targetFor, reduceMotion, width, x],
  )

  useEffect(() => {
    const controls = snap(index)
    return () => controls?.stop()
  }, [index, snap])

  const goTo = useCallback(
    (i: number) => setIndex(((i % count) + count) % count),
    [count],
  )
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count])
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + count) % count),
    [count],
  )

  const paused = hovered || touched || focusWithin || lightboxIndex !== null
  const autoplaying = autoplay && !reduceMotion && !paused

  useEffect(() => {
    if (!autoplaying) return
    const id = window.setInterval(next, AUTOPLAY_MS)
    return () => window.clearInterval(id)
  }, [autoplaying, next, index])

  const onDrag = (_: unknown, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 6) draggedRef.current = true
  }

  const onDragEnd = (_: unknown, info: PanInfo) => {
    const threshold = slideWidth * 0.15
    if (info.offset.x < -threshold || info.velocity.x < -500) {
      next()
    } else if (info.offset.x > threshold || info.velocity.x > 500) {
      prev()
    } else {
      snap(index)
    }
    // Let the click that follows pointerup see the drag, then reset.
    window.setTimeout(() => {
      draggedRef.current = false
    }, 0)
  }

  const openLightbox = (i: number) => {
    if (draggedRef.current) return
    setLightboxIndex(i)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      prev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      next()
    }
  }

  const regionHandlers = {
    onKeyDown,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    onTouchStart: () => setTouched(true),
    onFocus: () => setFocusWithin(true),
    onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        setFocusWithin(false)
      }
    },
  }

  return {
    viewportRef,
    x,
    width,
    slideWidth,
    index,
    goTo,
    next,
    prev,
    reduceMotion,
    autoplaying,
    dragConstraints: { left: targetFor(count - 1), right: targetFor(0) },
    onDrag,
    onDragEnd,
    regionHandlers,
    lightboxIndex,
    setLightboxIndex,
    openLightbox,
  }
}
